import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { useSimpleModal } from '@advanza/modal'
import { Button, LoadingDots, PreIcon } from '@advanza/ui'
import { fetchProvider } from 'actions/providers'
import { getDate } from 'date'
import { format } from 'date-fns'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import LeadSettingsData from 'interfaces/LeadSettings/LeadSettings'
import { hasAccess } from 'misc/user'
import { ChangeEvent, ReactNode, useState } from 'react'

export const MANUAL_PAUSE_REASON_REGULAR = 1
export const MANUAL_PAUSE_REASON_OVERDUE_INVOICE = 2

interface ProviderManualPauseProps {
    providerId: number
}

interface UseManualPauseResult {
    isSaving: boolean
    onChangeReason: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onPause: (manualPauseReason?: number) => Promise<void>
    pauseDate: string | null | undefined
    onResume: () => Promise<void>
    openModal: () => Promise<unknown>
    modal: ReactNode
}

function _useLeadSettings(providerId: number): Partial<LeadSettingsData> {
    const { leadSettings, providers } = useAppSelector((state) => state.providers.entities)
    const leadSettingId = providers?.[providerId]?.lead_setting
    if (!providers || leadSettingId === undefined) {
        return {}
    }
    return leadSettings?.[leadSettingId] ?? {}
}

function useManualPause(providerId: number): UseManualPauseResult {
    const { manual_pause: pauseDate } = _useLeadSettings(providerId)
    const [isSaving, setIsSaving] = useState(false)
    const [reason, setReason] = useState('')
    const onChangeReason = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setReason(e.target.value)
    const dispatch = useAppDispatch()
    const { openModal, renderModal } = useSimpleModal()

    const onPause = (manualPauseReason: number = MANUAL_PAUSE_REASON_REGULAR): Promise<void> => {
        setIsSaving(true)
        return call(`office/providers/enable-manual-pause/${providerId}/${manualPauseReason}`, {
            payload: { reason },
        })
            .then(
                () => {
                    dispatch(fetchProvider(providerId, true))
                },
                () => Promise.reject(),
            )
            .finally(() => {
                setIsSaving(false)
            })
    }

    const onResume = (): Promise<void> => {
        setIsSaving(true)
        return call('office/providers/remove-manual-pause/' + providerId, { payload: { reason } })
            .then(
                () => {
                    dispatch(fetchProvider(providerId, true))
                },
                () => Promise.reject(),
            )
            .finally(() => setIsSaving(false))
    }

    const modal = renderModal({
        header: 'Reason for manual pause',
        body: 'Please select the reason for the manual pause',
        buttons: [
            {
                text: 'Regular manual pause',
                func: () => onPause(MANUAL_PAUSE_REASON_REGULAR),
            },
            {
                text: 'Manual pause overdue invoice',
                func: () => onPause(MANUAL_PAUSE_REASON_OVERDUE_INVOICE),
            },
        ],
    })

    return {
        isSaving,
        onChangeReason,
        onPause,
        pauseDate,
        onResume,
        openModal,
        modal,
    }
}

const ProviderManualPause = ({ providerId }: ProviderManualPauseProps) => {
    const { pauseDate, isSaving, onResume, openModal, modal } = useManualPause(providerId)
    const canManageManualPause = hasAccess('admin', 'w')

    if (!canManageManualPause) {
        if (!pauseDate) {
            return null
        }
        return (
            <Row middle="xs">
                <Col x>
                    <em>On manual pause since {format(getDate(pauseDate), 'dd-MMM')}</em>
                </Col>
            </Row>
        )
    }

    const buttonLabel = (() => {
        if (isSaving) {
            return <LoadingDots />
        }
        return pauseDate ? 'Resume' : 'Set manual pause'
    })()

    return (
        <Row middle="xs">
            <Col x>
                <Button name="text" onClick={() => (pauseDate ? onResume() : openModal())}>
                    <PreIcon icon={pauseDate ? 'play_arrow' : 'pause'} primColor>
                        {buttonLabel}
                    </PreIcon>
                </Button>
                {modal}
            </Col>
            <Col x>
                {pauseDate && <em>On manual pause since {format(getDate(pauseDate), 'dd-MMM')}</em>}
            </Col>
        </Row>
    )
}

export default ProviderManualPause
